/**
 * Font Library セクション — フォルダ読み込みボタン + Latin フォント選択
 * dropdown をマウントする。
 *
 * 設計参照:
 * - spec §5.5（DEFAULT_FONTS / userFonts）
 * - spec §7（Font Library セクション、UI 順序）
 * - wireframe `wireframes/v0.10/wireframe.html` L220–232
 *
 * Phase J（F）: Japanese フォント dropdown を撤去し Latin 単独構成に整理した。
 * `AppState.japaneseFont` は残置（CJK 文字が含まれる本文を描画する際の
 * フォールバックとして text layer が参照する）。ユーザから直接触れなくなる
 * だけで、state としての存在は引き続き保証される。
 *
 * 挙動:
 * 1. `[↓ Load Fonts from Folder]` ボタン → hidden `<input type="file"
 *    webkitdirectory directory multiple>` を click。
 * 2. 選択後 `loadFontFolder(files)` を await → `store.userFonts` に append。
 * 3. status 行に `defaults only (N fonts)` / `N defaults + M user fonts`
 *    を表示（`DEFAULT_FONTS.length + userFonts.length` から算出）。
 * 4. Latin の `<select>` は DEFAULT_FONTS と userFonts を結合して option を
 *    張る。user 起源の option は先頭に `★ ` を付ける（Phase C 要件 8）。
 * 5. 選択変更で `store.latinFont` に CSS `font-family` 文字列（=
 *    `FontSpec.family`）を書き込む。
 * 6. userFonts / latinFont のいずれかが変わったら dropdown を全再構築
 *    （初版は全再生成で十分なパフォーマンス）。
 */

import { type AppState, DEFAULT_FONTS, type FontSpec, type Store } from '../../state';
import { loadFontFolder } from '../fontLoader';

/**
 * Font Library セクションをマウントする。
 *
 * @param root - セクションのコンテナ（`createSection(...)` で作ったもの）
 * @param store - グローバルストア
 * @returns teardown 関数。subscribe 解除 + DOM 撤去 + event listener 解放。
 */
export function mountFontLibrary(root: HTMLElement, store: Store<AppState>): () => void {
  // ── hidden file input（webkitdirectory）──────────────────────
  // `webkitdirectory` / `directory` 属性は HTMLInputElement の TS 型に
  // 無いため setAttribute で付与する（as any / @ts-expect-error なし）。
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.multiple = true;
  fileInput.setAttribute('webkitdirectory', '');
  fileInput.setAttribute('directory', '');
  fileInput.style.display = 'none';

  // ── Load ボタン ───────────────────────────────────────────
  const loadBtn = document.createElement('button');
  loadBtn.type = 'button';
  loadBtn.className = 'btn';
  // ↓ は Unicode 矢印（絵文字ではない）。wireframe と同表記。
  loadBtn.textContent = '↓ Load Fonts from Folder';

  // ── status 行 ─────────────────────────────────────────────
  const status = document.createElement('div');
  status.className = 'font-status';

  // ── Latin picker row ─────────────────────────────────────
  const latinRow = document.createElement('div');
  latinRow.className = 'font-picker-row';
  const latinLabel = document.createElement('label');
  latinLabel.textContent = 'Latin';
  const latinSelect = document.createElement('select');
  latinRow.appendChild(latinLabel);
  latinRow.appendChild(latinSelect);

  root.appendChild(fileInput);
  root.appendChild(loadBtn);
  root.appendChild(status);
  root.appendChild(latinRow);

  /**
   * `FontSpec` を <option> にする。user 起源には先頭に `★ ` を付ける。
   * `<option>.style.fontFamily` にプレビュー用に family を適用（ブラウザ
   * 依存で視覚のみ。Chrome/Safari は反映される）。
   */
  const makeOption = (font: FontSpec, selectedFamily: string): HTMLOptionElement => {
    const opt = document.createElement('option');
    opt.value = font.family;
    opt.textContent = font.source === 'user' ? `★ ${font.name}` : font.name;
    opt.style.fontFamily = font.family;
    if (font.family === selectedFamily) opt.selected = true;
    return opt;
  };

  /** status 文字列の生成（spec §5.5 / wireframe L224 と同表記）。 */
  const formatStatus = (userCount: number): string => {
    const defaults = DEFAULT_FONTS.length;
    if (userCount === 0) return `defaults only (${defaults} fonts)`;
    return `${defaults} defaults + ${userCount} user font${userCount === 1 ? '' : 's'}`;
  };

  /**
   * dropdown を state から全再構築する。
   * 初版は全削除→append の愚直実装。user が 100 件登録するようなケースは
   * 想定せず、遅延時は後続で diff 再構築を検討。Phase J で Japanese dropdown
   * は撤去されたため Latin のみ更新する。
   */
  const rebuild = (): void => {
    const state = store.get();
    const all: FontSpec[] = [...DEFAULT_FONTS, ...state.userFonts];

    latinSelect.textContent = '';
    for (const f of all) latinSelect.appendChild(makeOption(f, state.latinFont));

    status.textContent = formatStatus(state.userFonts.length);
  };

  rebuild();

  // ── event wiring ──────────────────────────────────────────
  const onLoadClick = (): void => {
    fileInput.click();
  };
  const onFileChange = async (): Promise<void> => {
    const files = fileInput.files;
    if (!files || files.length === 0) return;
    try {
      const loaded = await loadFontFolder(files);
      if (loaded.length === 0) return;
      const existing = store.get().userFonts;
      store.set({ userFonts: [...existing, ...loaded] });
    } finally {
      // 同じフォルダを再選択できるよう input をリセット
      fileInput.value = '';
    }
  };
  const onFileChangeWrapped = (): void => {
    // async の戻り値 Promise を発火のまま捨てると Biome に怒られるので
    // `void` 経由で明示的に無視する。
    void onFileChange();
  };
  const onLatinChange = (): void => {
    store.set({ latinFont: latinSelect.value });
  };

  loadBtn.addEventListener('click', onLoadClick);
  fileInput.addEventListener('change', onFileChangeWrapped);
  latinSelect.addEventListener('change', onLatinChange);

  const unsubscribe = store.subscribe((state, prev) => {
    // userFonts の参照が変わったとき、または latin の選択が外部から変わった
    // ときは dropdown を再構築する。Japanese は UI を撤去したため subscribe
    // 対象から除外（`japaneseFont` は text layer が直接読むだけ）。
    if (state.userFonts !== prev.userFonts || state.latinFont !== prev.latinFont) {
      rebuild();
    }
  });

  return (): void => {
    unsubscribe();
    loadBtn.removeEventListener('click', onLoadClick);
    fileInput.removeEventListener('change', onFileChangeWrapped);
    latinSelect.removeEventListener('change', onLatinChange);
    fileInput.remove();
    loadBtn.remove();
    status.remove();
    latinRow.remove();
  };
}
